import React, { useState, useEffect } from "react";

// libraries
import PropTypes from "prop-types";
import { useHistory, useParams } from "react-router";
import {
    CardNumberElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";

// components:
import CheckoutForm from "core/CheckoutForm";
import CheckoutHeader from "subscriptions/components/CheckoutHeader";
import SubscriptionLicenseBtns from "subscriptions/components/SubscriptionLicenseBtns";
import DisclaimerBodyText from "subscriptions/components/DisclaimerBodyText";
import { PayPalNounProSubProvider } from "frontend/context/PayPalNounProSubContext";

// reusable data / helpers:
import { getFormattedData } from "./helpers";
import { useAuth } from "frontend/hooks/useAuth";
import { getCsrfCookieName } from "helpers/gazetteGetCsrfCookieName";
import { getCookie } from "helpers/gazetteGetCookie";
import { usePayPalNounProSubContext } from "frontend/context/PayPalNounProSubContext";
import {
    CheckoutProvider,
    useCheckoutFormGeneralError,
    useUpdateBillingInfo
} from "frontend/context/CheckoutContext";

// queries & data
import { useQuery } from "@apollo/react-hooks";
import { GET_SAVED_CARD } from "photos/PhotoCheckout/queries";
import { MONTHLY_PRICE_PLAN, ANNUAL_PRICE_AS_YEARLY } from "core/constants";
import { ICON_CHECKOUT } from "data/links";
import {
    PRO_ANNUAL_DOWNLOAD,
    PRO_PREPAID_DOWNLOAD,
    PRO_SINGLE_DOWNLOAD,
    QUERY_PURCHASED
} from "icons/helpers";

import { GeneralFormErrorMessage } from "./styles";

const UnwrappedIconCheckout = ({
    title,
    thumbnail,
    slugWithId,
    iconId,
    iconDetailPageUrl
}) => {
    const { checkoutType: checkoutTypeParam } = useParams();

    const isSubscription = checkoutTypeParam !== PRO_SINGLE_DOWNLOAD;

    const history = useHistory();
    const { currentUser } = useAuth();
    const { submitBillingInfo, setReceiptInfo } = useUpdateBillingInfo();

    const { data: { savedCard: response = {} } = {} } = useQuery(
        GET_SAVED_CARD,
        {
            notifyOnNetworkStatusChange: true
        }
    );

    useEffect(() => {
        if (response && response.receiptInfo) {
            setReceiptInfo(response.receiptInfo);
        }
    }, [response]);

    useEffect(() => {
        if (!currentUser) return;
        // if user has subscription, always send them back to the icon detail page
        // so they can download the icon w/o seeing the checkout form
        if (currentUser.hasSubscription) {
            history.replace(iconDetailPageUrl);
        } else if (currentUser.hasPrepaidBalance && !isSubscription) {
            history.replace(
                ICON_CHECKOUT.replace(
                    ":checkoutType",
                    PRO_PREPAID_DOWNLOAD
                ).replace(":iconId", iconId)
            );
        }
    }, []);

    const {
        initialForm,
        hasSavedCard,
        itemPrice,
        iconDetailLink,
        isSubmitDisabled
    } = getFormattedData({
        slugWithId,
        response,
        isSubscription
    });

    // set up/assign const states:
    const [saveCard, setSaveCard] = useState(!!isSubscription);
    const [form, setForm] = useState(initialForm);
    const [licenseSelection, setLicenseSelection] = useState(checkoutTypeParam);
    const [loading, setLoading] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const {
        generalFormError,
        setGeneralFormError
    } = useCheckoutFormGeneralError();

    const submitFormWithNewCardInfo = async e => {
        e.preventDefault();

        if (!form.name) {
            return setGeneralFormError("Please fill out all required fields");
        }

        // sets spinner on purchase button
        setLoading(true);

        const cardNumberElement = elements.getElement(CardNumberElement);
        const { token, error } = await stripe.createToken(
            cardNumberElement,
            form
        );

        if (error) {
            setGeneralFormError(error.message);
            setLoading(false);
        }

        processTransaction(token);
    };

    const submitFormWithSavedCardInfo = e => {
        e.preventDefault();

        // sets spinner on purchase button
        setLoading(true);

        processTransaction({ id: null });
    };

    const handlePurchaseSuccess = () => {
        const iconNavURL = `${iconDetailPageUrl}?${QUERY_PURCHASED}=${licenseSelection ||
            PRO_SINGLE_DOWNLOAD}`;
        setLoading(false);
        history.replace(iconNavURL);
    };

    const processTransaction = async token => {
        const { id } = token;

        const csrfCookieName = getCsrfCookieName();
        const csrftoken = getCookie({ name: csrfCookieName });

        let body;
        let routeURL;
        // if license selection is true it means its either pro-yearly or pro-monthly
        // if false its an empty string and we will move forward with a basic icon purchase
        if (isSubscription) {
            routeURL = `/${currentUser.username}/subscribe/pro/`;
            body = { plan: licenseSelection };
            if (id) body.stripe_token = id;
        } else {
            routeURL = `/icon/${iconId}/purchase/`;
            body = {
                single_icon_price_cents: window._nounConfig.singleIconPriceCents
            };
            if (id) {
                body.stripe_token = id;
                body.save_card = saveCard;
            }
        }

        const finalPurchase = async () => {
            const purchaseOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrftoken
                },
                body: JSON.stringify(body)
            };
            const submitPurchase = await fetch(routeURL, purchaseOptions);

            if (submitPurchase.ok) {
                handlePurchaseSuccess();
            }
        };

        const isSuccess = await submitBillingInfo();
        if (isSuccess) finalPurchase();
    };

    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setGeneralFormError("");
    };

    const saveCardCheckboxOnClick = () => {
        setSaveCard(!saveCard);
    };

    // if user came from checkoutType + payment then we take the item
    // price which i believe is always 2.99. Need to check on that
    // otherwise we show cost based on toggle of licenseSelection
    const finalPrice = !isSubscription
        ? itemPrice
        : licenseSelection === PRO_ANNUAL_DOWNLOAD
        ? ANNUAL_PRICE_AS_YEARLY
        : MONTHLY_PRICE_PLAN;

    // this will need to be in a useState if we decide to add PromoCode
    // later on. I feel like we will but for now requirements say no
    const subscriptionPrices = {
        yearly: ANNUAL_PRICE_AS_YEARLY,
        monthly: MONTHLY_PRICE_PLAN
    };

    const {
        upgradeSuccessful: payPalUpgradeSuccessful,
        error: payPalUpgradeError
    } = usePayPalNounProSubContext();

    useEffect(() => {
        if (payPalUpgradeSuccessful) handlePurchaseSuccess();
        if (payPalUpgradeError) setGeneralFormError(payPalUpgradeError);
    }, [payPalUpgradeSuccessful, payPalUpgradeError]);

    return (
        <>
            {generalFormError && (
                <GeneralFormErrorMessage
                    handleDismissClick={() => setGeneralFormError("")}
                >
                    {generalFormError}
                </GeneralFormErrorMessage>
            )}

            {isSubscription && (
                <>
                    {/* Might want to put these two in core/components */}
                    <CheckoutHeader />

                    <SubscriptionLicenseBtns
                        subscriptionPrices={subscriptionPrices}
                        licenseSelection={licenseSelection}
                        setLicenseSelection={setLicenseSelection}
                    />
                </>
            )}

            <CheckoutForm
                cancelLink={iconDetailLink}
                onCheckoutPath
                form={form}
                handleFormChange={handleFormChange}
                hasSavedCard={hasSavedCard}
                hideCardLinks={isSubscription}
                isSubmitDisabled={isSubmitDisabled}
                isSubscription={isSubscription}
                itemPrice={finalPrice}
                itemPriceOrderSummaryItem={`$${itemPrice}`}
                itemTitle={title}
                licenseSelection={licenseSelection}
                loading={loading}
                orderSummaryTextLine1={title}
                orderSummaryTextLine2={"SVG, PNG"}
                response={response}
                saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                saveCardInitialValue={saveCard}
                showPayPalPayment={isSubscription}
                submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                submitFormWithSavedCardInfo={submitFormWithSavedCardInfo}
                thumbnail={thumbnail}
            />

            {isSubscription && (
                <DisclaimerBodyText licenseSelection={licenseSelection} />
            )}
        </>
    );
};

const IconCheckout = props => (
    <CheckoutProvider>
        <PayPalNounProSubProvider>
            <UnwrappedIconCheckout {...props} />
        </PayPalNounProSubProvider>
    </CheckoutProvider>
);

IconCheckout.propTypes = {
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    slugWithId: PropTypes.string.isRequired,
    iconId: PropTypes.string.isRequired,
    iconDetailPageUrl: PropTypes.string.isRequired
};

export default IconCheckout;
